import { Search, Download, Star, TrendingUp, Grid, List } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/components/ui/use-toast'
import { useNodeGrouping } from '@/hooks/useNodeGrouping'
import { BlueprintWithDetails, BLUEPRINT_CATEGORIES } from '@/types/blueprints'
import { getAxiosInstance } from '../../network/axiosInstance'
import { BlueprintCard } from './BlueprintCard'

export const MarketplacePage: React.FC = () => {
  const { toast } = useToast()
  const { createBlueprintContainer } = useNodeGrouping()
  const [blueprints, setBlueprints] = useState<BlueprintWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [activeTab, setActiveTab] = useState('all')
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'rating'>('popular')

  // Fetch marketplace blueprints
  const fetchMarketplaceBlueprints = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      
      if (searchTerm) params.append('search', searchTerm)
      if (selectedCategory !== 'all') params.append('category', selectedCategory)
      if (activeTab === 'featured') params.append('featured', 'true')
      if (activeTab === 'verified') params.append('verified', 'true')

      const response = await getAxiosInstance().get(`/api/marketplace/blueprints`, { params: Object.fromEntries(params) })
      const data = response.data
      const fetchedBlueprints = data.blueprints || []

      // Sort blueprints
      if (sortBy === 'popular') {
        fetchedBlueprints.sort((a: BlueprintWithDetails, b: BlueprintWithDetails) => 
          (b.marketplace?.downloadCount || 0) - (a.marketplace?.downloadCount || 0)
        )
      } else if (sortBy === 'newest') {
        fetchedBlueprints.sort((a: BlueprintWithDetails, b: BlueprintWithDetails) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
      } else if (sortBy === 'rating') {
        fetchedBlueprints.sort((a: BlueprintWithDetails, b: BlueprintWithDetails) => 
          (b.marketplace?.rating || 0) - (a.marketplace?.rating || 0)
        )
      }

      setBlueprints(fetchedBlueprints)
    } catch (error) {
      console.error('Error fetching marketplace blueprints:', error)
      toast({
        title: 'Error',
        description: 'Failed to load marketplace blueprints',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMarketplaceBlueprints()
  }, [searchTerm, selectedCategory, activeTab, sortBy])

  const handleDownloadBlueprint = async (blueprint: BlueprintWithDetails) => {
    try {      const response = await getAxiosInstance().post(`/api/marketplace/download/${blueprint.id}`)
      
      const data = response.data
      toast({
        title: 'Success',
        description: 'Blueprint downloaded successfully to your library'
      })
      
      // Optionally redirect to user's blueprints page
      console.log('Downloaded blueprint:', data)
    } catch (error) {
      console.error('Error downloading blueprint:', error)
      toast({
        title: 'Error',
        description: 'Failed to download blueprint',
        variant: 'destructive'
      })
    }
  }
  const handleRunBlueprint = (_blueprint: BlueprintWithDetails) => {
    // TODO: Open run blueprint dialog
    toast({
      title: 'Feature Coming Soon',
      description: 'Blueprint execution will be available soon'
    })
  }

  const handleAddToProject = (blueprint: BlueprintWithDetails) => {
    try {
      const containerId = createBlueprintContainer(blueprint, { x: 100, y: 100 })
      
      toast({
        title: 'Success',
        description: `Blueprint "${blueprint.name}" added to project as a container with ${blueprint.nodeDefinitions?.length || 0} nodes`
      })
      
      console.log('Created blueprint container:', containerId)
    } catch (error: any) {
      console.error('Error adding blueprint to project:', error)
      
      toast({
        title: 'Error',
        description: error.message || 'Failed to add blueprint to project',
        variant: 'destructive'
      })
    }
  }

  const handleDeleteBlueprint = (blueprint: BlueprintWithDetails) => {
    // For marketplace, we might not allow deletion or it might be different logic
    toast({
      title: 'Info',
      description: 'Marketplace blueprints cannot be deleted from here',
      variant: 'default'
    })
  }

  const filteredBlueprints = blueprints.filter(blueprint => {
    if (activeTab === 'featured') {
      return blueprint.marketplace?.featured
    }
    if (activeTab === 'verified') {
      return blueprint.marketplace?.verified
    }
    return true
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Blueprint Marketplace</h1>
          <p className="text-muted-foreground">
            Discover and download community-created AI workflow blueprints
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="flex items-center gap-1">
            <Download className="h-3 w-3" />
            {blueprints.reduce((sum, bp) => sum + (bp.marketplace?.downloadCount || 0), 0)} downloads
          </Badge>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search marketplace..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {BLUEPRINT_CATEGORIES.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={(value: 'popular' | 'newest' | 'rating') => setSortBy(value)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="popular">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Popular
              </div>
            </SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="rating">
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4" />
                Top Rated
              </div>
            </SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Blueprints</TabsTrigger>
          <TabsTrigger value="featured">Featured</TabsTrigger>
          <TabsTrigger value="verified">Verified</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {loading ? (
            <div className="text-center py-8">Loading marketplace blueprints...</div>
          ) : filteredBlueprints.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No blueprints found</p>
            </div>
          ) : (            <div className={
              viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }>
              {filteredBlueprints.map((blueprint) => (
                <BlueprintCard
                  key={blueprint.id}
                  blueprint={blueprint}
                  variant="marketplace"
                  onRun={handleRunBlueprint}
                  onDownload={handleDownloadBlueprint}
                  onDelete={handleDeleteBlueprint}
                  onAddToProject={handleAddToProject}
                />
              ))}
            </div>
          )}
        </TabsContent>        <TabsContent value="featured" className="space-y-4">
          {loading ? (
            <div className="text-center py-8">Loading featured blueprints...</div>
          ) : filteredBlueprints.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No featured blueprints found</p>
            </div>
          ) : (
            <div className={
              viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }>
              {filteredBlueprints.map((blueprint) => (
                <BlueprintCard
                  key={blueprint.id}
                  blueprint={blueprint}
                  variant="marketplace"
                  onRun={handleRunBlueprint}
                  onDownload={handleDownloadBlueprint}
                  onDelete={handleDeleteBlueprint}
                  onAddToProject={handleAddToProject}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="verified" className="space-y-4">
          {loading ? (
            <div className="text-center py-8">Loading verified blueprints...</div>
          ) : filteredBlueprints.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No verified blueprints found</p>
            </div>
          ) : (
            <div className={
              viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }>              {filteredBlueprints.map((blueprint) => (
                <BlueprintCard
                  key={blueprint.id}
                  blueprint={blueprint}
                  variant="marketplace"
                  onRun={handleRunBlueprint}
                  onDownload={handleDownloadBlueprint}
                  onDelete={handleDeleteBlueprint}
                  onAddToProject={handleAddToProject}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Stats */}
      <div className="flex justify-center pt-4">
        <div className="text-sm text-muted-foreground">
          Showing {filteredBlueprints.length} blueprint{filteredBlueprints.length !== 1 ? 's' : ''} from the marketplace
        </div>
      </div>
    </div>
  )
}
